import { Children, type ReactNode } from "react";
import { Box } from "./box";
import { DetailLabel } from "./detail-label";
import { tokens } from "./tokens";

export interface ResourceDetailsTableProps {
  title?: string;
  columns?: number;
  children: ReactNode;
}

function gridClasses(columns: number) {
  if (columns === 3) {
    return "lg:grid-cols-3 lg:[&>*:nth-child(-n+3)]:border-t-0 lg:[&>*:nth-child(n+4)]:border-t lg:[&>*:nth-child(n+4)]:border-black-100 lg:[&>*:not(:nth-child(3n+1))]:border-l lg:[&>*:not(:nth-child(3n+1))]:border-black-100 lg:[&>*:not(:nth-child(3n+1))]:pl-4 lg:[&>*:not(:nth-child(3n))]:pr-4";
  }
  if (columns >= 4) {
    return "lg:grid-cols-4 lg:[&>*:nth-child(-n+4)]:border-t-0 lg:[&>*:nth-child(n+5)]:border-t lg:[&>*:nth-child(n+5)]:border-black-100 lg:[&>*:not(:nth-child(4n+1))]:border-l lg:[&>*:not(:nth-child(4n+1))]:border-black-100 lg:[&>*:not(:nth-child(4n+1))]:pl-4 lg:[&>*:not(:nth-child(4n))]:pr-4";
  }
  return "lg:grid-cols-2 lg:[&>*:nth-child(-n+2)]:border-t-0 lg:[&>*:nth-child(n+3)]:border-t lg:[&>*:nth-child(n+3)]:border-black-100 lg:[&>*:not(:nth-child(2n+1))]:border-l lg:[&>*:not(:nth-child(2n+1))]:border-black-100 lg:[&>*:not(:nth-child(2n+1))]:pl-4 lg:[&>*:not(:nth-child(2n))]:pr-4";
}

export function ResourceDetailsTable({
  title = "Details",
  columns = 2,
  children,
}: ResourceDetailsTableProps) {
  const itemCount = Children.toArray(children).filter(Boolean).length;
  const remainder = itemCount % columns;
  const fillerCount = remainder === 0 ? 0 : columns - remainder;

  return (
    <Box size="md" className="flex flex-col gap-2">
      <h3 className={tokens.type["micro label strong"]}>{title}</h3>
      <div
        className={`grid grid-cols-1 [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-black-100 ${gridClasses(columns)}`}
      >
        {children}
        {Array.from({ length: fillerCount }, (_, i) => (
          <div key={`filler-${i}`} aria-hidden />
        ))}
      </div>
    </Box>
  );
}

export function ResourceDetailItem({
  title,
  titleTooltip,
  children,
  isLoading = false,
  className = "",
}: {
  title: string;
  titleTooltip?: string;
  children: ReactNode;
  isLoading?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col gap-0.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-2 ${className}`}
    >
      <DetailLabel
        as="h4"
        label={title}
        tooltip={titleTooltip}
        isLoading={isLoading}
        className="whitespace-nowrap"
      />
      <div
        className={`${tokens.type["detail value"]} break-words sm:text-right`}
      >
        {children}
      </div>
    </div>
  );
}
